import { useEffect, useMemo, useRef } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { motion, useReducedMotion, type Transition } from 'motion/react';
import type { ReflogProps } from '../contracts';
import { prefersReducedMotion, rowVariants, spring } from '../../lib/motion';
import { sound } from '../../lib/sound';
import { useStrings } from '../../state/useLocale';

/**
 * The reflog.
 *
 * Git's record of everywhere HEAD has been, and the way out of the mission
 * this whole product is named after. A row whose commit no longer appears in
 * the graph is not an error: it is the lost commit, still recoverable, and it
 * is marked `.unknown` rather than hidden.
 *
 * Entries are keyed by their distance from the *bottom* of the log, not by
 * `HEAD@{n}`, because the selector of every row shifts by one each time a
 * command runs. Keyed from the tail, an existing entry keeps its identity, so
 * a new command reads as one row arriving at the top and pushing the rest
 * down rather than the whole list rewriting itself.
 */

const LIMIT = 12;
const INSTANT: Transition = { duration: 0 };

export function Reflog({ git, selected, onSelect }: ReflogProps) {
  const s = useStrings().play.reflog;
  const reduced = (useReducedMotion() ?? prefersReducedMotion()) === true;
  const known = useMemo(() => new Set(git.commits.map((c) => c.hash)), [git.commits]);

  const total = git.reflog.length;
  const rows = git.reflog.slice(0, LIMIT).map((entry, i) => ({ entry, key: total - i }));

  // Only a row that was not on screen last time gets to arrive. Recorded in
  // an effect so a StrictMode double render cannot spend the animation early.
  const seen = useRef<Set<number> | null>(null);
  const previous = seen.current;
  useEffect(() => {
    seen.current = new Set(rows.map((r) => r.key));
  }, [git.reflog]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!git.isRepo || total === 0) {
    return <div className="graph-empty">{s.empty}</div>;
  }

  return (
    <>
      {rows.map(({ entry, key }) => {
        const isNew = previous !== null && !previous.has(key);
        const className = [
          'reflog-row',
          entry.hash === selected ? 'selected' : '',
          known.has(entry.hash) ? '' : 'unknown',
        ]
          .filter(Boolean)
          .join(' ');

        function select() {
          sound.click();
          // Toggle, so clicking the selected row closes the detail strip the
          // same way clicking the selected commit in the graph does.
          onSelect(entry.hash === selected ? null : entry.hash);
        }

        function onKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          select();
        }

        return (
          <motion.div
            key={key}
            className={className}
            data-hash={entry.hash}
            role="button"
            tabIndex={0}
            // A reflog row is `git reflog` output, whole: the selector, the
            // hash and the message are all the machine's, so the row keeps
            // the machine's direction in both languages (docs/i18n.md §7).
            dir="ltr"
            aria-pressed={entry.hash === selected}
            // `layout` is what makes the older rows slide down under the new
            // one instead of jumping a row height.
            layout={reduced ? false : 'position'}
            variants={rowVariants}
            initial={isNew && !reduced ? 'initial' : false}
            animate="animate"
            transition={reduced ? INSTANT : spring}
            onClick={select}
            onKeyDown={onKeyDown}
          >
            <code className="sel">{entry.selector}</code>
            <code className="hash">{entry.short}</code>
            <span className="msg">{entry.message}</span>
          </motion.div>
        );
      })}
    </>
  );
}

export default Reflog;
